import { FC } from 'react'
import { styled } from '@portfolio/styles'
import { SectionProjectCardGridProps } from '@portfolio/models'
import { Container, Link, Media, SectionWrapper } from '@portfolio/components'
import { useTranslation } from 'react-i18next'

const StyledSectionWrapper = styled(SectionWrapper, {
  backgroundColor: '$darkThemeBackground',
  color: '$white',
  display: 'grid',
  gap: '$96',
  '@md': {
    gap: '$120',
  },
  variants: {
    spacing: {
      default: {
        marginTop: '$mobileSectionMarginL',
        marginBottom: '$mobileSectionMarginL',
        '@md': {
          marginTop: '$desktopSectionMarginL',
          marginBottom: '$desktopSectionMarginL',
        },
      },
      medium: {
        marginTop: '$mobileSectionMarginM',
        marginBottom: '$mobileSectionMarginM',
        paddingBlock: '$24',
        gap: '$64',
        '@lg': {
          marginTop: '$desktopSectionMarginM',
          marginBottom: '$desktopSectionMarginM',
        },
      },
      firstSection: {
        paddingTop: 'calc($headerHeight + $mobileSectionMarginL)',
        paddingBottom: '$mobileSectionMarginL',
        '@md': {
          paddingTop: 'calc($headerHeight + $desktopSectionMarginL)',
          paddingBottom: '$desktopSectionMarginL',
        },
      },
      none: {
        marginTop: 0,
        marginBottom: 0,
      },
    },
    topBorder: {
      true: {
        borderTop: '1px solid $gray500',
        paddingBlock: '$24',
        gap: '$64',
      },
      false: {},
    },
  },
  defaultVariants: {
    spacing: 'default',
  },
})

const Title = styled('h2', {
  projectFont: 'heading04',
  fontSize: '36px',
  width: '100%',
  containerSpace: 'small',
  textAlign: 'left',

  '@xxl': {
    maxWidth: '$maxWidthDesktop',
    margin: 'auto',
    textAlign: 'center',
  },
})
const CardLink = styled(Link, {
  pointerEvents: 'all',
  zIndex: '$cardButton',
})
const ProjectsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  rowGap: '$72',
  '@lg': {
    gridTemplateColumns: '1fr 1fr',
    rowGap: '$168',
    columnGap: '1px',
  },
  variants: {
    single: {
      true: {
        '@lg': {
          gridTemplateColumns: 'minmax(0, 50vw)',
          justifyContent: 'center',
        },
      },
      false: {},
    },
  },
})
const ProjectCardWrap = styled('div', {
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  gap: '$32',
  [`& ${CardLink}`]: {
    opacity: 1,
  },
  '@lg': {
    gap: '$40',
    [`& ${CardLink}`]: {
      opacity: 0,
      transition: '250ms opacity',
    },
    '&:hover, &:focus-within': {
      [`& ${CardLink}`]: {
        opacity: 1,
        transition: '250ms opacity',
      },
    },
  },
})

const ContentWrap = styled(Container, {
  display: 'grid',
  gap: '$32',
  justifyItems: 'flex-start',
  color: '$white',
  gridColumn: 1,
  gridRow: 2,
  pointerEvents: 'none',
  '@lg': {
    alignContent: 'flex-start',
    gridTemplateColumns: '1fr auto',
    justifyContent: 'space-between',
  },
})
const Description = styled('h3', {
  projectFont: 'heading07',
  color: '$white_07',
  maxWidth: 480,
  zIndex: '$content',
})
const CaseStudyName = styled('span', {
  color: '$white',
  zIndex: '$content',
})

const LinkWrap = styled(Link, {
  gridColumn: 1,
  gridRow: '1 / -1',
  zIndex: '$content',
})
const MediaWrap = styled('div', {
  gridColumn: 1,
  gridRow: 1,
})

export const SectionProjectCardGrid: FC<SectionProjectCardGridProps> = ({
  title,
  spacing = 'default',
  projects,
  priority,
  topBorder,
}) => {
  const { t } = useTranslation('caseStudy')

  return (
    <StyledSectionWrapper
      type="NO_MARGIN"
      spacing={spacing}
      topBorder={topBorder}
    >
      {title && <Title>{title}</Title>}
      <ProjectsGrid single={projects?.length === 1}>
        {projects?.map((project, index) => (
          <ProjectCardWrap key={`card-${project.caseStudy?.name}-${index}`}>
            <MediaWrap>
              {project.media && (
                <Media
                  controlsPosition={'bottom-right'}
                  {...project.media}
                  loop
                  priority={priority && index < 4}
                  sizes="(min-width: 1000px) 50vw, 100vw"
                />
              )}
            </MediaWrap>
            <ContentWrap spaceSize={'small'}>
              {project.text && (
                <Description as={title ? 'h3' : 'h2'}>
                  <CaseStudyName>{project?.caseStudy?.name}</CaseStudyName>{' '}
                  {project.text}
                </Description>
              )}
              {project?.caseStudy?.path && (
                <CardLink
                  appearance={'outlinedWhite'}
                  href={project?.caseStudy?.path}
                >
                  {t('viewCaseStudy')}
                </CardLink>
              )}
            </ContentWrap>
            {project?.caseStudy?.path && (
              <LinkWrap
                tabIndex={-1}
                href={project?.caseStudy?.path}
                appearance="grid"
                label={`Case Study - ${project?.caseStudy?.name}`}
              />
            )}
          </ProjectCardWrap>
        ))}
      </ProjectsGrid>
    </StyledSectionWrapper>
  )
}
